import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, CircleNotch, ArrowRight, CheckCircle, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { platformApi, getPlatformApiErrorMessage } from '@/lib/platformApi';
import { ScrollLock } from '@/components/shared/ScrollLock';

/**
 * Transfer Ownership wizard — analyse everything the user owns, choose a
 * replacement barrister, preview exactly what will move, execute atomically.
 * Used standalone and, optionally, from the deactivation dialog — deactivation
 * itself never requires or performs a transfer.
 */

interface OwnershipCategory { key: string; label: string; count: number; reassignable: boolean }
interface DependencyReport {
  ownership: OwnershipCategory[];
  liveOwnershipCount: number;
}
interface Candidate { id: string; firstName: string; lastName: string; email: string; memberType: string }

const BARRISTER_TYPES = ['silk', 'junior', 'pupil'];

export function TransferOwnershipWizard({
  open, onClose, user, onDone,
}: {
  open: boolean;
  onClose: () => void;
  user: { id: string; chambersId: string; name: string };
  onDone?: () => void;
}) {
  const queryClient = useQueryClient();
  const [toUserId, setToUserId] = useState('');

  const depsQ = useQuery({
    queryKey: ['platform', 'users', user.id, 'dependencies'],
    queryFn: async () => {
      const res = await platformApi.get(`/users/${user.id}/dependencies`);
      return (res.data?.data ?? res.data) as DependencyReport;
    },
    enabled: open,
  });

  const candidatesQ = useQuery({
    queryKey: ['platform', 'users', 'barristers', user.chambersId],
    queryFn: async () => {
      const res = await platformApi.get('/users', { params: { chambersId: user.chambersId, limit: 100 } });
      const raw = res.data?.data ?? res.data;
      const list: Candidate[] = Array.isArray(raw) ? raw : (raw?.data ?? []);
      return list.filter((u) => BARRISTER_TYPES.includes(u.memberType) && u.id !== user.id);
    },
    enabled: open,
  });

  const transferMutation = useMutation({
    mutationFn: async () => {
      const res = await platformApi.post(`/users/${user.id}/transfer`, { toUserId });
      return (res.data?.data ?? res.data) as { totalMoved: number };
    },
    onSuccess: (r) => {
      toast.success(`Transferred ${r.totalMoved} item${r.totalMoved === 1 ? '' : 's'} of ownership`);
      queryClient.invalidateQueries({ queryKey: ['platform', 'users'] });
      onDone?.();
      onClose();
    },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err, 'Transfer failed')),
  });

  const reassignable = useMemo(
    () => (depsQ.data?.ownership ?? []).filter((o) => o.reassignable && o.count > 0),
    [depsQ.data],
  );
  const contextOnly = useMemo(
    () => (depsQ.data?.ownership ?? []).filter((o) => !o.reassignable && o.count > 0),
    [depsQ.data],
  );

  if (!open) return null;

  const candidates = candidatesQ.data ?? [];
  const chosen = candidates.find((c) => c.id === toUserId);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-plat-scrim/60 p-4 sm:items-center">
      <ScrollLock />
      <div className="w-full max-w-lg rounded-2xl border border-plat-border bg-plat-elevated shadow-2xl">
        <div className="flex items-center justify-between border-b border-plat-border px-5 py-4">
          <div>
            <h3 className="text-base font-semibold text-plat-fg">Transfer ownership</h3>
            <p className="text-xs text-plat-muted">Move {user.name}'s work to another barrister</p>
          </div>
          <button onClick={onClose} className="rounded p-1 text-plat-muted hover:bg-plat-hover hover:text-plat-secondary">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-5 px-5 py-5">
          {depsQ.isLoading ? (
            <div className="py-8 text-center"><CircleNotch className="mx-auto h-6 w-6 animate-spin text-plat-info-text" /></div>
          ) : (
            <>
              {/* What will move */}
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-plat-muted">Will move to the replacement</p>
                {reassignable.length === 0 ? (
                  <p className="rounded-lg border border-plat-border bg-plat-surface px-3 py-3 text-sm text-plat-muted">
                    Nothing to transfer — this user holds no live operational ownership.
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {reassignable.map((o) => (
                      <div key={o.key} className="flex items-center justify-between rounded-lg border border-plat-border bg-plat-surface px-3 py-2 text-sm">
                        <span className="text-plat-secondary">{o.label}</span>
                        <span className="font-semibold text-plat-fg">{o.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Context — stays as history / follows the matter */}
              {contextOnly.length > 0 && (
                <div>
                  <p className="mb-2 flex items-center gap-1.5 text-xs text-plat-muted">
                    <Warning className="h-3.5 w-3.5" /> Preserved as history (not rewritten)
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {contextOnly.map((o) => (
                      <span key={o.key} className="rounded-md bg-plat-surface px-2 py-1 text-xs text-plat-muted">
                        {o.label}: {o.count}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Replacement */}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Replacement barrister</label>
                {candidatesQ.isLoading ? (
                  <div className="py-3 text-center"><CircleNotch className="mx-auto h-5 w-5 animate-spin text-plat-info-text" /></div>
                ) : candidates.length === 0 ? (
                  <p className="rounded-lg border border-plat-warning-line bg-plat-warning-soft px-3 py-2 text-xs text-plat-warning-text">
                    No other barrister in this chambers to receive the transfer.
                  </p>
                ) : (
                  <select
                    value={toUserId}
                    onChange={(e) => setToUserId(e.target.value)}
                    className="h-9 w-full rounded-lg border border-plat-border bg-plat-sunken px-3 text-sm text-plat-fg focus:border-plat-focus focus:outline-none"
                  >
                    <option value="">Select a barrister…</option>
                    {candidates.map((c) => (
                      <option key={c.id} value={c.id}>{c.firstName} {c.lastName} — {c.email}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Preview line */}
              {chosen && reassignable.length > 0 && (
                <div className="flex items-center justify-center gap-2 rounded-lg border border-plat-info-line bg-plat-info-soft px-3 py-2.5 text-sm">
                  <span className="text-plat-secondary">{user.name}</span>
                  <ArrowRight className="h-4 w-4 text-plat-info-text" />
                  <span className="font-medium text-plat-fg">{chosen.firstName} {chosen.lastName}</span>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-plat-border px-5 py-4">
          <button onClick={onClose} className="rounded-lg border border-plat-border px-4 py-2 text-sm text-plat-secondary hover:bg-plat-hover">
            Cancel
          </button>
          <button
            disabled={!toUserId || reassignable.length === 0 || transferMutation.isPending}
            onClick={() => transferMutation.mutate()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-plat-accent-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover disabled:cursor-not-allowed disabled:bg-plat-neutral-soft disabled:text-plat-disabled"
          >
            {transferMutation.isPending ? <CircleNotch className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" weight="fill" />}
            Transfer & confirm
          </button>
        </div>
      </div>
    </div>
  );
}
